import { Box, Paper, Typography } from "@mui/material";
import { CheckCircleOutline as ClearIcon, WarningAmber as AttentionIcon } from "@mui/icons-material";
import MUIButton from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import type { ApproveStatus } from "../../utils/approveStatus";

const STICKY_STATUS_ID = "review-sticky-approve-status";

interface StickyApproveBarProps {
  status: ApproveStatus;
  isApproving: boolean;
  onApprove: () => void;
  /** Closes Compare with source so the checks sidebar is visible again. */
  onBackToChecks: () => void;
}

/**
 * Sticky "Before you approve" bar (#7431 AC3, artboard 1c).
 *
 * Shown only while Compare with source is open, which collapses the checks
 * sidebar to make room for the source pane. Without this the reviewer scrolls
 * a wide grid with no view of what is blocking approval and no Approve in
 * reach. It restates the same gate the header button uses (one derivation —
 * see approveStatus.ts) so the two can never disagree.
 */
export const StickyApproveBar = ({ status, isApproving, onApprove, onBackToChecks }: StickyApproveBarProps) => {
  const blocked = status.blocked || isApproving;
  return (
    <Paper
      elevation={0}
      role="region"
      aria-label="Before you approve"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (t) => t.zIndex.appBar - 1,
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1,
        mb: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        Before you approve
      </Typography>

      {status.kind === "attention" && (
        <Typography
          variant="body2"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "warning.dark", whiteSpace: "nowrap" }}
        >
          <AttentionIcon sx={{ fontSize: 16 }} />
          {status.attentionCount} {status.attentionCount === 1 ? "needs" : "need"} attention
        </Typography>
      )}
      {status.kind === "ready" && (
        <Typography
          variant="body2"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "success.main", whiteSpace: "nowrap" }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
          All checks pass
        </Typography>
      )}
      {status.kind !== "attention" && status.kind !== "ready" && (
        <Typography id={STICKY_STATUS_ID} variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {status.why}
        </Typography>
      )}

      {status.attentionNames.length > 0 && (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
          {status.attentionNames.join(" · ")}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />

      <MUIButton size={UIComponentSize.Small} variant={ButtonStyleType.Text} onClick={onBackToChecks}>
        Back to checks
      </MUIButton>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
        <MUIButton
          size={UIComponentSize.Small}
          variant={ButtonStyleType.Contained}
          onClick={onApprove}
          disabled={blocked}
          aria-describedby={status.why ? STICKY_STATUS_ID : undefined}
          sx={{ whiteSpace: "nowrap" }}
        >
          {isApproving ? "Approving..." : "Approve and load"}
        </MUIButton>
        {status.kind === "attention" && (
          <Typography id={STICKY_STATUS_ID} variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {status.why}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
